import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { IDevice } from "../../@types/Devices";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import { DEFAULT_PAGE_SIZE } from "../../constants/pagination";
import { devicesService } from "../../services/devicesService";

export function useDevices(patientId: number) {
  const { data, isError, isFetching, refetch, isRefetching } = useQuery<
    IDevice[]
  >({
    queryKey: [`devices-list-${patientId}`, patientId],
    queryFn: async () => {
      const response = await devicesService.getAllDevices(patientId);
      const responseData = response.data as
        | IDevice[]
        | IPaginatedResponse<IDevice[]>;

      // Handle both paginated response and direct array response
      if (Array.isArray(responseData)) {
        return responseData;
      }
      // If paginated response, extract the result array
      return responseData?.result ?? [];
    },
    refetchOnMount: true,
  });

  return {
    devices: data,
    isFetching,
    isError,
    refetch,
    isRefetching,
  };
}

type DevicesPaginatedResponse = IPaginatedResponse<IDevice[]>;

/**
 * Hook for fetching patient devices with infinite scrolling support.
 * Uses TanStack Query's useInfiniteQuery for paginated data fetching.
 *
 * @param patientId - The patient ID to fetch devices for
 * @param pageSize - Number of items per page (default: 20)
 */
export function useInfiniteDevices(
  patientId: number,
  pageSize: number = DEFAULT_PAGE_SIZE
) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ["patient-devices-infinite", patientId, pageSize],
    queryFn: async ({ pageParam }): Promise<DevicesPaginatedResponse> => {
      if (!patientId) {
        return {
          result: [],
          currentPage: 1,
          pageSize,
          pages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      }

      const response = await devicesService.getDevicesPaginated(
        patientId,
        pageParam,
        pageSize
      );

      return response.data as DevicesPaginatedResponse;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: DevicesPaginatedResponse) => {
      if (lastPage.hasNextPage) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
  });

  // Flatten all pages into a single array of devices
  const devices =
    data?.pages.flatMap((page: DevicesPaginatedResponse) => {
      const items = page.result || [];
      return items.map((deviceData: IDevice) => {
        return {
          ...deviceData,
        } as IDevice;
      });
    }) || [];

  // Get total count from the first page
  const totalCount =
    data?.pages[0]?.pages !== undefined
      ? data.pages[0].pages * pageSize
      : devices.length;

  return {
    devices,
    totalCount,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  };
}
